import type { TemplatePage } from "../builder";
import type { LibraryTemplate } from "../library";
import { ellipse, line, logo, photo, pill, rect, stack, star, t, text } from "./shorthands";

/** Instagram-Posts und Karussell. */
const FIRST_POSTS: LibraryTemplate[] = [
  {
    id: "post-neuigkeit",
    title: "Große Neuigkeit",
    description: "Kräftige Farbfläche, große Überschrift, Platz für Details",
    presetId: "instagram-portrait",
    topic: "ankuendigung",
    style: "klar",
    pages: [
      {
        background: "primary",
        nodes: [
          ellipse({ x: 640, y: -220, w: 680, h: 680, fill: "accent", name: "Kreis" }),
          ellipse({
            x: 520,
            y: 250,
            w: 260,
            h: 260,
            stroke: { color: "on:primary/60", width: 6 },
            name: "Ring",
          }),
          logo({ x: 90, y: 90, w: 300, h: 90, on: "primary" }),
          stack({
            x: 90,
            y: 1250,
            w: 880,
            anchor: "bottom",
            gap: [28, 40],
            items: [
              t("NEU BEI UNS", "subline", 34, "on:primary/80"),
              t("Wir haben Neuigkeiten!", "heading-1", 112, "on:primary"),
              t(
                "Ab 1. Oktober findet ihr uns an neuer Adresse. Alle Infos gibt’s über den Link in der Bio.",
                "body",
                36,
                "on:primary/85",
              ),
            ],
          }),
        ],
      },
    ],
  },
  {
    id: "post-event-foto",
    title: "Event mit Foto",
    description: "Großes Foto, Datum als Badge, Titel und Eckdaten",
    presetId: "instagram-portrait",
    topic: "event",
    style: "warm",
    pages: [
      {
        background: "background",
        nodes: [
          photo({ x: 60, y: 60, w: 960, h: 720, radius: "lg" }),
          rect({
            x: 110,
            y: 640,
            w: 230,
            h: 250,
            fill: "primary",
            radius: "md",
            shadow: { y: 16, blur: 40, color: "#00000033" },
            name: "Datum",
          }),
          text({
            text: "24",
            style: "heading-1",
            size: 116,
            color: "on:primary",
            x: 110,
            y: 650,
            w: 230,
            h: 150,
            align: "center",
          }),
          text({
            text: "OKT",
            style: "subline",
            size: 40,
            color: "on:primary",
            x: 110,
            y: 796,
            w: 230,
            h: 60,
            align: "center",
          }),
          stack({
            x: 380,
            y: 820,
            w: 640,
            gap: 14,
            items: [
              t("Sommerfest im Hof", "heading-1", 76, "text"),
              t("Samstag · 18 Uhr · Hofgarten 12", "body", 32, "text-muted"),
            ],
          }),
          stack({
            x: 90,
            y: 1270,
            w: 640,
            anchor: "bottom",
            items: [t("Eintritt frei – wir freuen uns auf euch!", "subline", 34, "primary")],
          }),
          logo({ x: 760, y: 1195, w: 230, h: 80, on: "background", align: "right" }),
        ],
      },
    ],
  },
  {
    id: "post-zitat",
    title: "Zitat",
    description: "Ruhiges Zitat mit großem Anführungszeichen",
    presetId: "square",
    topic: "zitat",
    style: "elegant",
    pages: [
      {
        background: "surface",
        nodes: [
          text({
            text: "“",
            style: "quote",
            size: 360,
            color: "primary/25",
            x: 70,
            y: 0,
            w: 320,
            name: "Anführungszeichen",
          }),
          stack({
            x: 120,
            y: 540,
            w: 840,
            anchor: "center",
            gap: [40, 28],
            items: [
              t("Gutes Design ist so wenig Design wie möglich.", "quote", 70, "text"),
              { kind: "shape", shape: "rect", w: 90, h: 6, fill: "accent", name: "Linie" },
              t("Dieter Rams, Designer", "subline", 32, "text-muted"),
            ],
          }),
          logo({ x: 120, y: 910, w: 260, h: 80, on: "surface" }),
        ],
      },
    ],
  },
  {
    id: "post-sale",
    title: "Rabatt-Aktion",
    description: "Laute Aktion mit Stern-Badge für den Rabatt",
    presetId: "square",
    topic: "angebot",
    style: "laut",
    pages: [
      {
        background: "accent",
        nodes: [
          stack({
            x: 80,
            y: 150,
            w: 600,
            gap: [16, 28],
            items: [
              t("NUR DIESES WOCHENENDE", "subline", 32, "text"),
              t("Sommer\nSale", "heading-1", 200, "text", { shrink: true }),
              t("Auf alles im Laden – und online mit dem Code SOMMER20.", "body", 34, "text"),
            ],
          }),
          star({
            x: 620,
            y: 560,
            w: 420,
            h: 420,
            sides: 14,
            innerRatio: 0.8,
            rotation: 10,
            fill: "primary",
            name: "Stern",
          }),
          text({
            text: "-20\u00a0%",
            style: "heading-1",
            size: 130,
            color: "on:primary",
            // The inner circle of the star.
            x: 690,
            y: 630,
            w: 280,
            h: 280,
            rotation: 10,
            align: "center",
          }),
          logo({ x: 80, y: 930, w: 240, h: 80, on: "accent" }),
        ],
      },
    ],
  },
  {
    id: "post-tipps",
    title: "3 Tipps",
    description: "Nummerierte Liste mit Überschrift – ideal für Wissensposts",
    presetId: "instagram-portrait",
    topic: "wissen",
    style: "verspielt",
    pages: [
      {
        background: "background",
        nodes: [
          ellipse({ x: 860, y: -120, w: 340, h: 340, fill: "accent/40", name: "Kreis" }),
          stack({
            x: 90,
            y: 110,
            w: 800,
            gap: 18,
            items: [
              t("WISSEN KOMPAKT", "subline", 30, "primary"),
              t("3 Tipps für bessere Fotos mit dem Handy", "heading-1", 84, "text"),
            ],
          }),
          ...(
            [
              [
                "Licht von der Seite",
                "Stell dich nah ans Fenster – weiches Tageslicht schmeichelt jedem Motiv.",
              ],
              ["Linse putzen", "Klingt banal, macht aber den größten Unterschied bei der Schärfe."],
              ["Raster einschalten", "Mit der Drittel-Regel wirken Bilder sofort ausgewogener."],
            ] as const
          ).flatMap(([title, body], index) => {
            const y = 540 + index * 225;
            return [
              ellipse({ x: 90, y, w: 110, h: 110, fill: "primary", name: `Nummer ${index + 1}` }),
              text({
                text: String(index + 1),
                style: "heading-1",
                size: 54,
                color: "on:primary",
                x: 90,
                y,
                w: 110,
                h: 110,
                align: "center",
              }),
              stack({
                x: 240,
                y: y + 4,
                w: 750,
                gap: 8,
                items: [t(title, "subline", 40, "text"), t(body, "body", 30, "text-muted")],
              }),
            ];
          }),
          logo({ x: 90, y: 1230, w: 240, h: 70, on: "background" }),
          text({
            text: "Speichern nicht vergessen!",
            style: "caption",
            size: 28,
            x: 500,
            y: 1250,
            w: 490,
            align: "right",
          }),
        ],
      },
    ],
  },
  {
    id: "post-team",
    title: "Team-Vorstellung",
    description: "Rundes Porträt, Name, Rolle und ein paar persönliche Worte",
    presetId: "instagram-portrait",
    topic: "team",
    style: "warm",
    pages: [
      {
        background: "surface",
        nodes: [
          text({
            text: "UNSER TEAM",
            style: "subline",
            size: 30,
            color: "primary",
            x: 90,
            y: 90,
            w: 900,
            align: "center",
          }),
          photo({ x: 265, y: 170, w: 550, h: 550, ellipse: true, fill: "primary/20" }),
          ellipse({ x: 750, y: 190, w: 110, h: 110, fill: "accent", name: "Punkt" }),
          ellipse({ x: 230, y: 620, w: 60, h: 60, fill: "primary", name: "Punkt" }),
          stack({
            x: 90,
            y: 790,
            w: 900,
            gap: [10, 24],
            align: "center",
            items: [
              t("Das ist Lena", "heading-1", 88, "text"),
              t("Leitung Kundenservice", "subline", 40, "primary"),
              t(
                "Seit 2019 im Team, liebt Bergtouren, guten Kaffee und knifflige Fragen.",
                "body",
                32,
                "text-muted",
              ),
            ],
          }),
          logo({ x: 390, y: 1200, w: 300, h: 80, on: "surface", align: "center" }),
        ],
      },
    ],
  },
  {
    id: "post-job",
    title: "Wir stellen ein",
    description: "Stellenanzeige mit Eckdaten und Bewerben-Button",
    presetId: "instagram-portrait",
    topic: "team",
    style: "klar",
    pages: [
      {
        background: "secondary",
        nodes: [
          ellipse({ x: 620, y: 760, w: 700, h: 700, fill: "primary/35", name: "Kreis" }),
          ellipse({ x: 820, y: 660, w: 160, h: 160, fill: "accent", name: "Punkt" }),
          ...pill(90, 110, 420, 72, "WIR SUCHEN DICH", "accent", 28),
          logo({ x: 720, y: 110, w: 270, h: 72, on: "secondary", align: "right" }),
          stack({
            x: 90,
            y: 250,
            w: 860,
            gap: 44,
            items: [
              t("Grafikdesigner:in (m/w/d)", "heading-1", 84, "on:secondary"),
              t(
                "• Vollzeit oder Teilzeit\n• Modernes Team in Köln\n• Start ab sofort",
                "body",
                40,
                "on:secondary/85",
              ),
            ],
          }),
          ...pill(90, 1080, 500, 110, "Jetzt bewerben", "primary", 40, { radius: "md" }),
          text({
            text: "jobs@beispiel.de",
            style: "caption",
            size: 30,
            color: "on:secondary/70",
            x: 90,
            y: 1225,
            w: 600,
          }),
        ],
      },
    ],
  },
  {
    id: "post-produkt",
    title: "Produkt-Highlight",
    description: "Produktfoto, Preis-Badge und kurze Beschreibung",
    presetId: "instagram-portrait",
    topic: "produkt",
    style: "natur",
    pages: [
      {
        background: "background",
        nodes: [
          photo({ x: 0, y: 0, w: 1080, h: 840 }),
          ellipse({
            x: 790,
            y: 700,
            w: 230,
            h: 230,
            fill: "accent",
            shadow: { y: 12, blur: 36, color: "#00000033" },
            name: "Preis",
          }),
          text({
            text: "19,90\u00a0€",
            style: "heading-2",
            size: 50,
            color: "on:accent",
            x: 800,
            y: 700,
            w: 210,
            h: 230,
            align: "center",
          }),
          stack({
            x: 90,
            y: 910,
            w: 660,
            gap: [12, 18],
            items: [
              t("NEU IM SORTIMENT", "subline", 28, "primary"),
              t("Kräutertee Waldlichtung", "heading-1", 76, "text"),
              t(
                "Bio, handverlesen und frisch abgefüllt – jetzt im Laden und online.",
                "body",
                30,
                "text-muted",
              ),
            ],
          }),
          logo({ x: 790, y: 1240, w: 200, h: 60, on: "background", align: "right" }),
        ],
      },
    ],
  },
  {
    id: "post-zahl",
    title: "Zahl des Monats",
    description: "Eine große Zahl mit Einordnung – für Erfolge und Fakten",
    presetId: "square",
    topic: "wissen",
    style: "nacht",
    pages: [
      {
        background: "background",
        nodes: [
          ellipse({ x: 760, y: 760, w: 420, h: 420, fill: "primary/15", name: "Kreis" }),
          rect({ x: 90, y: 120, w: 110, h: 12, fill: "accent", name: "Linie" }),
          logo({ x: 740, y: 80, w: 250, h: 70, on: "background", align: "right" }),
          stack({
            x: 90,
            y: 190,
            w: 900,
            gap: 28,
            items: [
              t("87\u00a0%", "heading-1", 290, "primary"),
              t("unserer Kundinnen und Kunden empfehlen uns weiter.", "subline", 54, "text"),
            ],
          }),
          text({
            text: "Quelle: Kundenumfrage 2026, 1.200 Antworten",
            style: "caption",
            size: 26,
            color: "text-muted",
            x: 90,
            y: 960,
            w: 700,
          }),
        ],
      },
    ],
  },
  {
    id: "post-vorher-nachher",
    title: "Vorher / Nachher",
    description: "Zwei Fotos nebeneinander – für Handwerk, Umbau und Ergebnisse",
    presetId: "square",
    topic: "produkt",
    style: "klar",
    pages: [
      {
        background: "background",
        nodes: [
          photo({ x: 40, y: 40, w: 490, h: 800, radius: "md", name: "Bild vorher" }),
          photo({ x: 550, y: 40, w: 490, h: 800, radius: "md", name: "Bild nachher" }),
          ...pill(70, 750, 200, 60, "VORHER", "secondary", 24),
          ...pill(580, 750, 220, 60, "NACHHER", "primary", 24),
          stack({
            x: 40,
            y: 880,
            w: 700,
            gap: 12,
            items: [
              t("Badsanierung in drei Wochen", "heading-2", 52, "text"),
              t("Mehr Projekte auf unserer Website", "caption", 28, "text-muted"),
            ],
          }),
          logo({ x: 780, y: 885, w: 260, h: 80, on: "background", align: "right" }),
        ],
      },
    ],
  },
  {
    id: "post-frage",
    title: "Frage an die Community",
    description: "Großes Fragezeichen, kurze Frage – bringt Kommentare",
    presetId: "square",
    topic: "community",
    style: "verspielt",
    pages: [
      {
        background: "primary",
        nodes: [
          text({
            text: "?",
            style: "heading-1",
            size: 700,
            color: "on:primary/12",
            x: 540,
            y: 20,
            w: 560,
            rotation: 10,
            align: "center",
            name: "Fragezeichen",
          }),
          stack({
            x: 90,
            y: 540,
            w: 800,
            anchor: "center",
            gap: [24, 40],
            items: [
              t("FRAGE DER WOCHE", "subline", 30, "on:primary/80"),
              t("Kaffee oder Tee – was trinkt ihr bei der Arbeit?", "heading-1", 84, "on:primary"),
              t("Schreibt es uns in die Kommentare!", "body", 36, "on:primary/85"),
            ],
          }),
          logo({ x: 90, y: 930, w: 260, h: 80, on: "primary" }),
        ],
      },
    ],
  },
  {
    id: "karussell-ratgeber",
    title: "Karussell: Ratgeber",
    description: "Vier Seiten: Titel, zwei Tipps und Abschluss zum Wischen",
    presetId: "instagram-portrait",
    topic: "wissen",
    style: "klar",
    pages: [
      {
        name: "Titel",
        background: "primary",
        nodes: [
          ellipse({ x: -220, y: -220, w: 700, h: 700, fill: "on:primary/10", name: "Kreis" }),
          logo({ x: 90, y: 90, w: 280, h: 80, on: "primary" }),
          stack({
            x: 90,
            y: 1170,
            w: 900,
            anchor: "bottom",
            gap: 24,
            items: [
              t("RATGEBER", "subline", 32, "on:primary/80"),
              t("So gelingt dein erster Social-Media-Plan", "heading-1", 104, "on:primary"),
            ],
          }),
          text({
            text: "Weiter wischen",
            style: "caption",
            size: 30,
            color: "on:primary",
            x: 600,
            y: 1240,
            w: 340,
            align: "right",
          }),
          {
            kind: "shape",
            shape: "triangle",
            x: 955,
            y: 1245,
            w: 30,
            h: 30,
            rotation: 90,
            fill: "on:primary",
            name: "Pfeil",
          },
        ],
      },
      ...(
        [
          [
            "01",
            "Ziele festlegen",
            "Was willst du erreichen? Mehr Reichweite, mehr Anfragen oder eine starke Community? Schreib dir ein bis zwei konkrete Ziele auf – daran misst du später jeden Beitrag.",
            "Ein Ziel, das du messen kannst, ist ein gutes Ziel.",
          ],
          [
            "02",
            "Themen sammeln",
            "Notiere wiederkehrende Themen: Einblicke hinter die Kulissen, Tipps aus deinem Fachgebiet, Kundenstimmen und Aktionen. So gehen dir die Ideen nicht aus.",
            "Drei feste Rubriken reichen für den Anfang.",
          ],
        ] as const
      ).map(
        ([number, title, body, note], index): TemplatePage => ({
          name: title,
          background: "background",
          nodes: [
            text({
              text: number,
              style: "heading-1",
              size: 200,
              color: "primary/20",
              x: 90,
              y: 80,
              w: 500,
              name: "Nummer",
            }),
            stack({
              x: 90,
              y: 340,
              w: 900,
              gap: 28,
              items: [t(title, "heading-2", 76, "text"), t(body, "body", 38, "text")],
            }),
            rect({ x: 90, y: 860, w: 900, h: 280, fill: "surface", radius: "md", name: "Merke" }),
            rect({ x: 90, y: 860, w: 12, h: 280, fill: "primary", name: "Linie" }),
            stack({
              x: 150,
              y: 1000,
              w: 790,
              anchor: "center",
              gap: 14,
              items: [t("MERKE", "subline", 28, "primary"), t(note, "heading-2", 50, "text")],
            }),
            logo({ x: 90, y: 1235, w: 200, h: 60, on: "background" }),
            text({
              text: `${index + 2} / 4`,
              style: "caption",
              size: 28,
              color: "text-muted",
              x: 790,
              y: 1248,
              w: 200,
              align: "right",
            }),
          ],
        }),
      ),
      {
        name: "Abschluss",
        background: "secondary",
        nodes: [
          stack({
            x: 90,
            y: 600,
            w: 900,
            anchor: "center",
            gap: 28,
            align: "center",
            items: [
              t("Hat’s geholfen?", "heading-1", 100, "on:secondary"),
              t(
                "Speichere den Beitrag und folge uns für mehr Tipps rund um Marketing.",
                "body",
                38,
                "on:secondary/85",
              ),
            ],
          }),
          ...pill(310, 940, 460, 100, "Jetzt folgen", "accent", 36),
          logo({ x: 390, y: 1180, w: 300, h: 80, on: "secondary", align: "center" }),
        ],
      },
    ],
  },
];

const MORE_POSTS: LibraryTemplate[] = [
  {
    id: "post-gewinnspiel",
    title: "Gewinnspiel",
    description: "Preis-Foto, große Ansage und die drei Schritte zum Mitmachen",
    presetId: "instagram-portrait",
    topic: "community",
    style: "warm",
    tags: ["gewinnspiel", "verlosung", "giveaway", "gewinnen", "mitmachen", "aktion"],
    pages: [
      {
        background: "primary",
        nodes: [
          ellipse({ x: 950, y: 196, w: 56, h: 56, fill: "accent", name: "Konfetti" }),
          ellipse({ x: 34, y: 470, w: 36, h: 36, fill: "on:primary/40", name: "Konfetti" }),
          ellipse({ x: 1010, y: 560, w: 44, h: 44, fill: "accent/80", name: "Konfetti" }),
          ...pill(90, 90, 330, 70, "GEWINNSPIEL", "accent", 30),
          logo({ x: 690, y: 90, w: 300, h: 70, on: "primary", align: "right" }),
          text({
            text: "Wir verlosen 3 Gutscheine à 50\u00a0€",
            style: "heading-1",
            size: 92,
            color: "on:primary",
            x: 90,
            y: 200,
            w: 900,
            h: 300,
          }),
          photo({ x: 90, y: 520, w: 900, h: 400, radius: "lg", fill: "on:primary/15" }),
          rect({ x: 90, y: 950, w: 900, h: 270, fill: "background", radius: "lg", name: "Karte" }),
          text({
            text: "So machst du mit",
            style: "subline",
            size: 32,
            color: "primary",
            x: 130,
            y: 975,
            w: 820,
            h: 50,
          }),
          ...["Folge unserem Account", "Like diesen Beitrag", "Markiere eine Freundin"].flatMap(
            (step, index) => {
              const x = 130 + index * 280;
              return [
                ellipse({
                  x,
                  y: 1045,
                  w: 64,
                  h: 64,
                  fill: "primary",
                  name: `Schritt ${index + 1}`,
                }),
                text({
                  text: String(index + 1),
                  style: "heading-2",
                  size: 34,
                  color: "on:primary",
                  x,
                  y: 1045,
                  w: 64,
                  h: 64,
                  align: "center",
                }),
                text({
                  text: step,
                  style: "body",
                  size: 28,
                  color: "text",
                  x,
                  y: 1122,
                  w: 250,
                  h: 80,
                }),
              ];
            },
          ),
          text({
            text: "Teilnahme bis 15. Oktober · Teilnahmebedingungen über den Link in der Bio",
            style: "caption",
            size: 24,
            color: "on:primary/85",
            x: 90,
            y: 1250,
            w: 900,
            align: "center",
          }),
        ],
      },
    ],
  },
  {
    id: "post-oeffnungszeiten",
    title: "Öffnungszeiten",
    description: "Übersichtliche Zeiten-Tabelle mit Adresse – quadratisch für Feed und Profil",
    presetId: "square",
    topic: "ankuendigung",
    style: "natur",
    tags: ["öffnungszeiten", "geschäftszeiten", "sprechzeiten", "laden", "praxis", "feiertage"],
    pages: [
      {
        background: "background",
        nodes: [
          rect({ x: 0, y: 0, w: 1080, h: 330, fill: "primary", name: "Farbfläche" }),
          stack({
            x: 90,
            y: 165,
            w: 900,
            anchor: "center",
            gap: 14,
            items: [
              t("SO SIND WIR FÜR EUCH DA", "subline", 30, "on:primary/80"),
              t("Unsere Öffnungszeiten", "heading-1", 84, "on:primary"),
            ],
          }),
          text({
            text: "Montag – Freitag\nSamstag\nSonntag",
            style: "subline",
            size: 44,
            color: "text",
            x: 90,
            y: 400,
            w: 560,
            name: "Tage",
          }),
          text({
            text: "8 – 18 Uhr\n9 – 14 Uhr\ngeschlossen",
            style: "subline",
            size: 44,
            color: "text",
            x: 650,
            y: 400,
            w: 340,
            align: "right",
            name: "Zeiten",
          }),
          line(90, 650, 900, "text-muted/40", 2),
          text({
            text: "Termine gern auch nach Vereinbarung – ruf uns einfach an.",
            style: "body",
            size: 34,
            color: "text-muted",
            x: 90,
            y: 690,
            w: 900,
          }),
          logo({ x: 90, y: 900, w: 280, h: 90, on: "background" }),
          text({
            text: "Lindenstraße 8 · 50674 Köln\n0221 123 456",
            style: "caption",
            size: 28,
            color: "text-muted",
            x: 420,
            y: 905,
            w: 570,
            align: "right",
          }),
        ],
      },
    ],
  },
  {
    id: "post-kundenstimme",
    title: "Kundenstimme",
    description: "Bewertung mit fünf Sternen, Zitat und Porträt – für Vertrauen im Feed",
    presetId: "square",
    topic: "zitat",
    style: "klar",
    tags: ["testimonial", "bewertung", "rezension", "sterne", "feedback", "referenz", "google"],
    pages: [
      {
        background: "surface",
        nodes: [
          rect({
            x: 80,
            y: 80,
            w: 920,
            h: 920,
            fill: "background",
            radius: "lg",
            shadow: { y: 20, blur: 60, color: "#0000001a" },
            name: "Karte",
          }),
          ...[0, 1, 2, 3, 4].map((index) =>
            star({
              x: 150 + index * 84,
              y: 160,
              w: 66,
              h: 66,
              sides: 5,
              innerRatio: 0.45,
              fill: "accent",
              name: "Stern",
            }),
          ),
          text({
            text: "„Super Beratung, schnelle Umsetzung und ein Ergebnis, das uns jeden Tag Freude macht.“",
            style: "quote",
            size: 58,
            color: "text",
            x: 150,
            y: 280,
            w: 780,
            h: 420,
          }),
          photo({ x: 150, y: 760, w: 150, h: 150, ellipse: true, fill: "primary/20" }),
          stack({
            x: 330,
            y: 835,
            w: 380,
            anchor: "center",
            gap: 4,
            items: [
              t("Jana Hoffmann", "subline", 36, "text"),
              t("Inhaberin, Blumen Hoffmann", "caption", 26, "text-muted"),
            ],
          }),
          logo({ x: 730, y: 800, w: 200, h: 70, on: "background", align: "right" }),
        ],
      },
    ],
  },
  {
    id: "karussell-schritte",
    title: "Karussell: Schritt für Schritt",
    description: "Fünf Seiten: Titel, drei Schritte mit Foto und eine Seite zum Speichern",
    presetId: "instagram-portrait",
    topic: "wissen",
    style: "verspielt",
    tags: ["karussell", "anleitung", "schritte", "slides", "swipe", "how-to", "carousel"],
    pages: [
      {
        name: "Titel",
        background: "primary",
        nodes: [
          ellipse({ x: 640, y: -200, w: 640, h: 640, fill: "accent", name: "Kreis" }),
          logo({ x: 90, y: 90, w: 280, h: 80, on: "primary" }),
          stack({
            x: 90,
            y: 1150,
            w: 900,
            anchor: "bottom",
            gap: 24,
            items: [
              t("SCHRITT FÜR SCHRITT", "subline", 32, "on:primary/80"),
              t("In 3 Schritten zum eigenen Newsletter", "heading-1", 108, "on:primary"),
            ],
          }),
          ...pill(90, 1200, 320, 72, "Wischen  →", "background", 28),
        ],
      },
      ...(
        [
          [
            "Ziel festlegen",
            "Überleg dir, wem dein Newsletter hilft und wie oft er erscheint. Einmal im Monat reicht für den Anfang völlig.",
          ],
          [
            "Anmeldung einrichten",
            "Ein Formular auf der Website, ein Hinweis in der Bio – und die Einwilligung sauber dokumentieren.",
          ],
          [
            "Erste Ausgabe schreiben",
            "Ein Thema, ein Bild, ein klarer Link. Lieber kurz und regelmäßig als lang und selten.",
          ],
        ] as const
      ).map(
        ([title, body], index): TemplatePage => ({
          name: `Schritt ${index + 1}`,
          background: "background",
          nodes: [
            photo({ x: 0, y: 0, w: 1080, h: 560 }),
            ellipse({
              x: 90,
              y: 490,
              w: 140,
              h: 140,
              fill: "primary",
              name: `Nummer ${index + 1}`,
            }),
            text({
              text: String(index + 1),
              style: "heading-1",
              size: 72,
              color: "on:primary",
              x: 90,
              y: 490,
              w: 140,
              h: 140,
              align: "center",
            }),
            stack({
              x: 90,
              y: 680,
              w: 900,
              gap: 28,
              items: [t(title, "heading-1", 80, "text"), t(body, "body", 38, "text-muted")],
            }),
            text({
              text: `${index + 2} / 5`,
              style: "caption",
              size: 28,
              color: "text-muted",
              x: 790,
              y: 1240,
              w: 200,
              align: "right",
            }),
            logo({ x: 90, y: 1220, w: 220, h: 70, on: "background" }),
          ],
        }),
      ),
      {
        name: "Abschluss",
        background: "secondary",
        nodes: [
          ellipse({ x: -220, y: 900, w: 640, h: 640, fill: "primary/40", name: "Kreis" }),
          stack({
            x: 90,
            y: 560,
            w: 900,
            anchor: "center",
            gap: 30,
            align: "center",
            items: [
              t("Hilfreich?", "heading-1", 120, "on:secondary"),
              t(
                "Speichere den Beitrag für später und teile ihn mit jemandem, der gerade seinen ersten Newsletter plant.",
                "body",
                38,
                "on:secondary/85",
              ),
            ],
          }),
          ...pill(290, 900, 500, 100, "Folge uns für mehr", "accent", 34),
          logo({ x: 390, y: 1150, w: 300, h: 80, on: "secondary", align: "center" }),
        ],
      },
    ],
  },
];

export const POSTS: LibraryTemplate[] = [...FIRST_POSTS, ...MORE_POSTS];
